// third-party
import { createSlice } from '@reduxjs/toolkit';
import { devConsolelog } from 'helpers/utilityFunctions';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { returnFiltersQueryString } from 'helpers/objectFunctions';
import { dispatch } from 'store';
import axios from 'utils/axios';
import { BOOKING_TYPE, CATEGORY_ACCOMODATION, CATEGORY_FLIGHTS, PAX_TYPE } from 'views/pages/booking-requests/constant';
import { openSnackbar } from './snackbar';

const DEFAULT_ERROR_MESSAGE = 'Something Went Wrong';

const initialState = {
    error: null,
    data: {},
    utilities: {},
    statusUtilities: {},
    requestCount: {
        allRequests: 0,
        agentRequests: 0,
        failed: 0,
        pending: 0,
        manualRequests: 0
    },
    commentsData: {
        commentsList: [],
        nextPageUrl: '',
        isScrollToBottom: true
    },
    status: {},
    bookingData: {},
    isLoading: false,
    autoSuggestData: {},
    filters: {
        // oc_status: [{ id: 'open', name: 'open' }]
    },
    tabFilters: {
        // oc_status: [{ id: 'open', name: 'open' }]
    },
    chatTabValue: 0
};

const slice = createSlice({
    name: 'bookingRequests',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        setCurrentTab(state, action) {
            devConsolelog(`countdata in object`, action.payload);
            state.currentTabValue = action.payload;
        },

        // setData
        setData(state, action) {
            const { count = 0, next = null, results = {}, page = 0 } = action.payload || {};
            const { utilities = {}, service_request_list: serviceRequestList = [] } = results || {};

            state.data.count = count;
            state.data.next = next;
            state.utilities = utilities;
            state.data.serviceRequestList = serviceRequestList;
            state.data.currentPage = page;
        },

        setFilters(state, action) {
            state.filters = action.payload || {};
        },

        setTabFilters(state, action) {
            state.tabFilters = action.payload || {};
        },

        setCountData(state, action) {
            const { count = {} } = action.payload || {};
            const {
                total = '',
                agent_requests: agentRequests = '',
                failed_requests: failedRequests = '',
                pending_requests: pendingRequests = '',
                manual_requests: manualRequests = ''
            } = count || {};

            state.requestCount.allRequests = total;
            state.requestCount.agentRequests = agentRequests;
            state.requestCount.failed = failedRequests;
            state.requestCount.pending = pendingRequests;
            state.requestCount.manualRequests = manualRequests;
        },

        setCommentsData(state, action) {
            const { data = {}, isRefresh = false } = action.payload;
            const { next = null, results = [] } = data || {};
            if (isRefresh) {
                state.commentsData.commentsList = results.reverse();
                state.commentsData.isScrollToBottom = true;
            } else {
                state.commentsData.commentsList = [...results.reverse(), ...state.commentsData.commentsList];
                state.commentsData.isScrollToBottom = false;
            }
            state.commentsData.nextPageUrl = next;
        },

        setIsLoading(state, action) {
            const { loadingField = '', isLoading = false } = action.payload || {};
            state[loadingField] = isLoading;
        },

        setPostCommentsData(state, action) {
            state.commentsData.commentsList = [...state.commentsData.commentsList, action.payload];
            state.commentsData.isScrollToBottom = true;
        },

        setBookingData(state, action) {
            const {
                data = {},
                internal_status: internalStatus = [],
                service_request_status: serviceRequestStatus = [],
                oc_code: ocCode = '',
                oc_id: ocId = ''
            } = action.payload || {};
            state.bookingData = { ...state.bookingData, ...data, ocCode, ocId };
            state.statusUtilities = { internal_status: internalStatus, service_request_status: serviceRequestStatus };
        },

        setClearBookingData(state) {
            state.bookingData = {};
            state.commentsData = { commentsList: [], nextPageUrl: '', isScrollToBottom: true };
        },

        setFlightData(state, action) {
            state.bookingData.booking_details = action.payload || {};
        },

        setHotelsData(state, action) {
            state.bookingData.booking_details = action.payload || {};
        },

        setPaxData(state, action) {
            const { results = {} } = action.payload || {};
            state.bookingData.pax_details = results;
        },

        setCurrentPageUrl(state, action) {
            state.data.currentPageUrl = action.payload;
        },

        setClearServiceRequestData(state) {
            state.data.serviceRequestList = [];
        },

        setAutosuggestAssigneeData(state, action) {
            const { results = [] } = action.payload;

            const autosuggestDropdownData = results.map((data) => {
                const { id = '', first_name: firstName = '', last_name: lastName = '', email = '', username = '' } = data || {};

                return { firstName, lastName, email, username, id, label: `${id} | ${username} | ${email}` };
            });

            state.autosuggestAssigneeData = autosuggestDropdownData;
        },

        setSupplierAutosuggestData(state, action) {
            devConsolelog('auto suggest data ', state, action.payload);
            const {
                code__completion: memCodeData = [],
                company_name__completion: companyNameData = [],
                email__completion: emailData = [],
                name__completion: nameData = []
            } = action.payload;

            const autosuggestData = [
                memCodeData[0]?.options,
                nameData[0]?.options,
                companyNameData[0]?.options,
                emailData[0]?.options
            ].find((arr) => arr.length);

            const autosuggestDropdownData = autosuggestData.map((data) => {
                const { _source: source = {} } = data || {};
                const { name = '', code = '', email = '', company_name: companyName = '' } = source || {};

                return { code, name, email, companyName, label: `${code} ${name} ${email} ${companyName}` };
            });

            state.autoSuggestData.supplierDropdownData = autosuggestDropdownData;
        },

        setCurrentChatTabValue(state, action) {
            state.chatTabValue = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

export function getBookingRequestsData(pageChangeUrl = '', filterType = '', newPage = 0, pageSize = 25) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingRequestLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
            if (newPage !== 0) dispatch(slice.actions.setCurrentPageUrl(pageChangeUrl));
        }
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);

            if (pageChangeUrl && filterQueryString) {
                url = `${url}&${filterQueryString}`;
            } else if (filterQueryString) {
                url = `${url}?${filterQueryString}`;
            }
            if (newPage === 0) dispatch(slice.actions.setCurrentPageUrl(url));
        }
        if (url?.includes('?')) {
            url += `&page_size=${pageSize}`;
        } else {
            url += `?page_size=${pageSize}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setData({ ...response.data, page: newPage }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingRequestLoading', isLoading: false }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setClearServiceRequestData());
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingRequestLoading', isLoading: false }));
        }
    };
}

export function getBookingRequestsCount(filterType = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingRequestCountLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/?count=true`;
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);
            if (filterQueryString) {
                url = `${url}&${filterQueryString}`;
            }
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response countData', response.data);
            dispatch(slice.actions.setCountData({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingRequestCountLoading', isLoading: false }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingRequestCountLoading', isLoading: false }));
        }
    };
}

export function getCommentsData(cancellationCode = '', nextPageUrl = '', isRefresh = false) {
    if (!nextPageUrl) {
        dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: true }));
    }
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/${cancellationCode}/comments/`;
        if (nextPageUrl) url = nextPageUrl;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData', response.data);
            dispatch(slice.actions.setCommentsData({ data: response.data, isRefresh }));
            if (!nextPageUrl) {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: false }));
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            if (!nextPageUrl) {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: false }));
            }
        }
    };
}

export function getInternalCommentsData(ticketCode = '', nextPageUrl = '', isRefresh = false) {
    if (!nextPageUrl) {
        dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: true }));
    }
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${ticketCode}/conversations/`;
        if (nextPageUrl) url = nextPageUrl;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData', response.data);
            dispatch(slice.actions.setCommentsData({ data: response.data, isRefresh }));
            if (!nextPageUrl) {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: false }));
            }
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            if (!nextPageUrl) {
                dispatch(slice.actions.setIsLoading({ loadingField: 'isCommentLoading', isLoading: false }));
            }
        }
    };
}

export function getBookingInfo(cancellationCode = '', filterType = '', type = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingInfoLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/${cancellationCode}/`;
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);
            devConsolelog('filter url', filterQueryString, filterType);
            if (filterQueryString) {
                url = `${url}?${filterQueryString}`;
            }
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            if (type === PAX_TYPE) {
                dispatch(slice.actions.setPaxData({ ...response.data }));
            } else dispatch(slice.actions.setBookingData({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingInfoLoading', isLoading: false }));
            return response.data;
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingInfoLoading', isLoading: false }));
            throw error;
        }
    };
}

export function getFlightHotelDetail(filterType = '', type = '', bookingCategory = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isFlightHotelLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}bms/bookings/`;
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);
            devConsolelog('filter url', filterQueryString, filterType);
            if (filterQueryString) {
                url = `${url}?${filterQueryString}`;
            }
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData', bookingCategory);
            if (type === BOOKING_TYPE) {
                if (bookingCategory === CATEGORY_FLIGHTS) {
                    const { booking_details: bookingDetails = {} } = response?.data || {};
                    dispatch(slice.actions.setFlightData({ ...bookingDetails }));
                } else if (bookingCategory === CATEGORY_ACCOMODATION) {
                    const { results = {} } = response?.data || {};
                    dispatch(slice.actions.setHotelsData({ ...results }));
                }
            }
            dispatch(slice.actions.setIsLoading({ loadingField: 'isFlightHotelLoading', isLoading: false }));
        } catch (error) {
            if (bookingCategory === CATEGORY_FLIGHTS) {
                dispatch(slice.actions.setFlightData({ error }));
            } else if (bookingCategory === CATEGORY_ACCOMODATION) {
                dispatch(slice.actions.setHotelsData({ error }));
            }

            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};

            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isFlightHotelLoading', isLoading: false }));
        }
    };
}

export function postComment(cancellationCode = '', multipartFormData = {}) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/${cancellationCode}/comments/`;
        try {
            const response = await axios.post(url, multipartFormData, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData ', response.data);
            dispatch(slice.actions.setPostCommentsData({ ...response.data }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function postInternalComment(ticketCode = '', multipartFormData = {}) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/oc-system/${ticketCode}/conversations/`;
        try {
            const response = await axios.post(url, multipartFormData, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getCommentsData ', response.data);
            dispatch(slice.actions.setPostCommentsData({ ...response.data }));
        } catch (error) {
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function patchCurrentStatus(code, updatedStatus, btnClicked) {
    return async () => {
        devConsolelog('ticketId from params', code, updatedStatus);
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/${code}/`;
        try {
            const response = await axios.patch(url, updatedStatus, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response ', response.data);
            dispatch(slice.actions.hasError(''));
            await dispatch(
                openSnackbar({
                    open: true,
                    message: btnClicked === 1 || btnClicked === 3 ? 'Saved' : 'Saved and Closed',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getSupplierAutosuggestData(searchedVal = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}organization/suggest?code__completion=${searchedVal}&name__completion=${searchedVal}&email__completion=${searchedVal}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response getOrgAutosuggestData', response.data);
            dispatch(slice.actions.setSupplierAutosuggestData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getAutosuggestAssigneeData(str = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/users?search=${str}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setAutosuggestAssigneeData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function postManualRequest(data) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}bms/service-requests/`;
        try {
            const response = await axios.post(url, data, {
                headers: {
                    source: 'mweb'
                }
            });
            devConsolelog('response postManualRequestData', response.data);
            const { cancellation_code: cancellationCode = '', booking = {} } = response?.data || {};
            const { code: bookingCode = '' } = booking || {};
            if (bookingCode && cancellationCode) {
                window.open(`/booking-list/${bookingCode}?serviceRequestCode=${cancellationCode}`, '_blank');
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export const filterTabs =
    (filter = '') =>
    async () => {
        dispatch(slice.actions.setTabFilters(filter));
    };

export const setLoadingState =
    (isLoading = false) =>
    () => {
        dispatch(slice.actions.setIsLoading(isLoading));
    };

export const filterRequestData =
    (filter = '') =>
    async () => {
        dispatch(slice.actions.setFilters(filter || initialState?.filters));
    };

export const clearData = () => () => {
    dispatch(slice.actions.setClearBookingData());
};

export const updateChatTabValue = (val) => () => {
    dispatch(slice.actions.setCurrentChatTabValue(val));
};
